/**
 * GoogleRestrictionDialog Component
 *
 * A dialog that displays Google Ads account restrictions and eligibility requirements.
 * Used when operations fail due to Google-imposed limitations on new or ineligible accounts.
 */

import React, { useEffect, useRef } from 'react';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from './Button';

export interface GoogleRestrictionDetails {
  title: string;
  description: string;
  requirements?: string[];
  note?: string;
  help_url: string;
}

interface GoogleRestrictionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  details: GoogleRestrictionDetails;
}

export const GoogleRestrictionDialog: React.FC<GoogleRestrictionDialogProps> = ({
  isOpen,
  onClose,
  details,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Body scroll locking to prevent layout shift from scrollbar
  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleLearnMore = () => {
    window.open(details.help_url, '_blank', 'noopener,noreferrer');
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="restriction-dialog-title"
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" aria-hidden="true" />

      {/* Dialog */}
      <div
        ref={dialogRef}
        className="relative w-full max-w-lg transform overflow-hidden rounded-xl bg-white p-6 text-left shadow-xl transition-all"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close dialog"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-amber-100">
            <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" />
          </div>
          <div className="flex-1 pr-6">
            <h3
              id="restriction-dialog-title"
              className="text-lg font-semibold leading-6 text-gray-900"
            >
              {details.title}
            </h3>
            <p className="mt-2 text-sm text-gray-600">
              {details.description}
            </p>
          </div>
        </div>

        {/* Requirements list */}
        {details.requirements && details.requirements.length > 0 && (
          <div className="mt-4 ml-16">
            <ul className="list-disc list-outside space-y-1.5 text-sm text-gray-600 pl-4">
              {details.requirements.map((requirement, index) => (
                <li key={index}>{requirement}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Note */}
        {details.note && (
          <div className="mt-4 ml-16 p-3 bg-blue-50 rounded-lg border border-blue-100">
            <p className="text-sm text-blue-800">
              <span className="font-medium">Tip:</span> {details.note}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="mt-6 flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={handleLearnMore}
          >
            Learn More
          </Button>
          <Button
            variant="primary"
            onClick={onClose}
          >
            Got It
          </Button>
        </div>
      </div>
    </div>
  );
};
